import {
	IdpSyncSkipReason,
	MappedUserField,
	type MappingPreviewFieldDto,
	OrgUnitMatchBy,
	Role,
	SyncFieldOutcome,
	TransformType,
	UnknownOrgUnitPolicy,
} from '@today/types'

/** Row labels of the mapped TODAY fields (spec identity-attribute-mapping-ui REQ-203). */
export const FIELD_LABELS: Readonly<Record<MappedUserField, string>> = {
	[MappedUserField.EMAIL]: 'Email',
	[MappedUserField.DISPLAY_NAME]: 'Display name',
	[MappedUserField.GIVEN_NAME]: 'Given name',
	[MappedUserField.FAMILY_NAME]: 'Family name',
	[MappedUserField.PICTURE]: 'Picture',
	[MappedUserField.PHONE]: 'Phone',
	[MappedUserField.LOCALE]: 'Locale',
	[MappedUserField.EMPLOYEE_ID]: 'Employee ID',
}

export const TRANSFORM_OPTIONS = [
	{ id: TransformType.NONE, text: 'None' },
	{ id: TransformType.TRIM, text: 'Trim' },
	{ id: TransformType.LOWERCASE, text: 'Lowercase' },
	{ id: TransformType.SPLIT_FIRST, text: 'Split first' },
	{ id: TransformType.REGEX_EXTRACT, text: 'Regex extract' },
	{ id: TransformType.LOOKUP, text: 'Lookup' },
]

export const MATCH_BY_OPTIONS = [
	{ id: OrgUnitMatchBy.CODE, text: 'Unit code' },
	{ id: OrgUnitMatchBy.EXTERNAL_ID, text: 'External ID' },
]

/** The two IdP-mappable roles, with the TO-388 branch labels (REQ-205). */
export const MAPPABLE_ROLE_OPTIONS = [
	{ id: Role.ORG_USER, text: 'Branch user (Gestor de oficina)' },
	{ id: Role.ORG_MANAGER, text: 'Branch manager (Director de oficina)' },
]

const SHORT_ROLE_LABELS: Partial<Record<Role, string>> = {
	[Role.ORG_USER]: 'Branch user',
	[Role.ORG_MANAGER]: 'Branch manager',
}

/** A role's short label in the preview; roles a sync never touches keep their code. */
export const roleLabel = (role: Role): string => SHORT_ROLE_LABELS[role] ?? role

/** A role set as one phrase: "Branch user", "Branch user, ORG_ADMIN", or "no role". */
export const rolesLabel = (roles: Role[]): string => (roles.length > 0 ? roles.map(roleLabel).join(', ') : 'no role')

export const POLICY_LABELS: Readonly<Record<UnknownOrgUnitPolicy, string>> = {
	[UnknownOrgUnitPolicy.REJECT_LOGIN]: 'Unknown units: Reject the login',
	[UnknownOrgUnitPolicy.NO_ORG_UNIT]: 'Unknown units: Sign in without an org unit',
	[UnknownOrgUnitPolicy.DEFAULT_ORG_UNIT]: 'Unknown units: Place in the default unit',
}

const SKIP_LABELS: Partial<Record<SyncFieldOutcome, string>> = {
	[SyncFieldOutcome.SKIPPED_EMPTY]: 'Empty',
	[SyncFieldOutcome.SKIPPED_CONFLICT]: 'Already used by another user',
	[SyncFieldOutcome.UNMAPPED]: 'Not mapped',
}

/** The preview value of a field row: the derived value, or its outcome label (REQ-203). */
export function previewValueLabel(entry: MappingPreviewFieldDto | undefined): string {
	if (!entry) return '—'
	if (entry.outcome === SyncFieldOutcome.SKIPPED_INVALID) {
		return entry.reason === IdpSyncSkipReason.NOT_A_URL ? 'Not a URL' : 'Invalid'
	}
	return SKIP_LABELS[entry.outcome] ?? entry.value ?? '—'
}

/** A `changedFields` path (`fields.LOCALE`, `orgUnit`, `roles`) as a label (REQ-208). */
export function changedFieldLabel(path: string): string {
	if (path.startsWith('fields.')) return FIELD_LABELS[path.slice('fields.'.length) as MappedUserField] ?? path
	if (path === 'orgUnit') return 'Org unit'
	if (path === 'roles') return 'Roles'
	return path
}

/** An ISO timestamp as `YYYY-MM-DD HH:MM UTC` — deterministic, no locale. */
export const utcMinute = (iso: string): string => `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`
